import type { Orientation, SpreadType } from './types';

export interface DemoCardSelection {
  cardIndex: number;
  orientation: Orientation;
}

const TOTAL_CARDS = 78;

function mulberry32(seed: number): () => number {
  return function () {
    let t = (seed += 0x6D2B79F5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashToSeed(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

export function getSpreadCardCount(spread: SpreadType): number {
  switch (spread) {
    case 'one-card':
      return 1;
    case 'three-card':
      return 3;
    case 'celtic-cross':
      return 10;
    default:
      return 3;
  }
}

export function generateDemoCardSelections(seedKey: string, spread: SpreadType): DemoCardSelection[] {
  const targetCount = getSpreadCardCount(spread);
  const rng = mulberry32(hashToSeed(seedKey));
  const picked = new Set<number>();
  const selections: DemoCardSelection[] = [];

  while (selections.length < targetCount) {
    const cardIndex = Math.floor(rng() * TOTAL_CARDS);
    if (picked.has(cardIndex)) continue;

    picked.add(cardIndex);
    const orientation: Orientation = rng() < 0.7 ? 'upright' : 'reversed';
    selections.push({ cardIndex, orientation });
  }

  return selections;
}

function getDemoCardStorageKey(readingId: string): string {
  return `demo_cards_${readingId}`;
}

function isValidSelection(value: unknown): value is DemoCardSelection {
  if (!value || typeof value !== 'object') return false;
  const item = value as { cardIndex?: unknown; orientation?: unknown };
  return typeof item.cardIndex === 'number'
    && item.cardIndex >= 0
    && item.cardIndex < TOTAL_CARDS
    && (item.orientation === 'upright' || item.orientation === 'reversed');
}

export function readDemoCardSelections(readingId: string, spread: SpreadType): DemoCardSelection[] | null {
  if (typeof window === 'undefined') return null;

  const raw = sessionStorage.getItem(getDemoCardStorageKey(readingId));
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return null;

    const expectedCount = getSpreadCardCount(spread);
    if (parsed.length !== expectedCount) return null;

    if (!parsed.every(isValidSelection)) return null;
    return parsed as DemoCardSelection[];
  } catch {
    return null;
  }
}

export function writeDemoCardSelections(readingId: string, selections: DemoCardSelection[]): void {
  if (typeof window === 'undefined') return;
  sessionStorage.setItem(getDemoCardStorageKey(readingId), JSON.stringify(selections));
}
